import React from 'react';
import { useTranslation } from 'react-i18next';
import { Truck, Clock, CheckCircle, Info, Package } from 'lucide-react';

export default function DeliveryTab({ 
  formData, 
  allDeliverySlots, 
  selectedDeliverySlots, 
  onDeliverySlotToggle 
}) {
  const { t } = useTranslation();

  const formatTime = (hours) => {
    if (hours === null || hours === undefined) return t('settings.noTimeSet');
    return `${hours.toString().padStart(2, '0')}:00`;
  };

  const formatTimeRange = (startTime, endTime) => {
    if (startTime === null || startTime === undefined || endTime === null || endTime === undefined) {
      return t('settings.invalidTimeRange');
    }
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-orange-100 rounded-lg mr-4">
            <Truck className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{t('settings.tabs.delivery')}</h3>
            <p className="text-sm text-gray-600">{t('settings.delivery.description')}</p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-blue-200 overflow-hidden">
        <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
          <h4 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <Info className="w-5 h-5" />
            {t('settings.deliverySlotInfo')}
          </h4>
        </div>
        <div className="p-6">
          <p className="text-sm text-blue-700">{t('settings.deliverySlotDescription')}</p>
        </div>
      </div>

      {/* Delivery Slots */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t('settings.deliverySlots')}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{t('settings.selectAvailableTimeSlots')}</p>
        </div>
        
        <div className="p-6">
          {allDeliverySlots.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">{t('settings.noDeliverySlots')}</h4>
              <p className="text-gray-600">{t('settings.noDeliverySlotsMessage')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allDeliverySlots.map((slot) => (
                <label key={slot.id} className="cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedDeliverySlots.includes(slot.id)}
                    onChange={() => onDeliverySlotToggle(slot.id)}
                    className="sr-only"
                  />
                  <div className={`
                    rounded-lg p-6 border-2 transition-all duration-200 cursor-pointer hover:shadow-md
                    ${selectedDeliverySlots.includes(slot.id) 
                      ? 'bg-orange-600 text-white border-orange-600 shadow-lg transform scale-105' 
                      : 'bg-white hover:bg-gray-50 border-gray-300 hover:border-orange-300'
                    }
                  `}>
                    <div className="text-center">
                      <div className="mb-3">
                        <Clock className={`w-8 h-8 mx-auto ${
                          selectedDeliverySlots.includes(slot.id) ? 'text-white' : 'text-orange-500'
                        }`} />
                      </div>
                      <h3 className={`font-semibold text-lg mb-2 ${
                        selectedDeliverySlots.includes(slot.id) ? 'text-white' : 'text-gray-900'
                      }`}>
                        {formatTimeRange(slot.hoursStartTime, slot.hoursEndTime)}
                      </h3>
                      <p className={`text-sm ${
                        selectedDeliverySlots.includes(slot.id) ? 'text-orange-100' : 'text-gray-500'
                      }`}>
                        {t('settings.deliveryWindow')}
                      </p>
                      
                      {selectedDeliverySlots.includes(slot.id) && (
                        <div className="mt-3">
                          <CheckCircle className="w-6 h-6 mx-auto text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Delivery Slots Summary */}
      {selectedDeliverySlots.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-green-200 overflow-hidden">
          <div className="bg-green-50 px-6 py-4 border-b border-green-200">
            <h4 className="text-lg font-semibold text-green-900 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {t('settings.selectedDeliverySlots')} ({selectedDeliverySlots.length})
            </h4>
          </div>
          
          <div className="p-6">
            <div className="flex flex-wrap gap-3">
              {allDeliverySlots
                .filter(slot => selectedDeliverySlots.includes(slot.id))
                .map((slot) => (
                  <span key={slot.id} className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                    <Clock className="w-4 h-4 mr-2" />
                    {formatTimeRange(slot.hoursStartTime, slot.hoursEndTime)}
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}

      {selectedDeliverySlots.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">{t('settings.noSlotsSelected')}</h4>
            <p className="text-gray-600">{t('settings.selectDeliverySlotsToStart')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
